import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../services/api';
import { API, formatError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  Spinner, ShieldCheck
} from '@phosphor-icons/react';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [email, setEmail] = useState(location.state?.prefillEmail || '');
  const [password, setPassword] = useState(location.state?.prefillPassword || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const demoBanner = location.state?.demoBanner;

  // P0 hotfix — Trust reset.
  // Some users (especially iOS Safari home-screen PWA installs) end up with a
  // stale service worker holding old auth-bundle JS that fails to read the new
  // login response. This handler hard-resets every piece of client state so the
  // next page load is clean: SW unregistered, all caches purged, localStorage
  // cleared (we ONLY clear our own kindra_* keys to be polite to other origins),
  // sessionStorage cleared, then the page hard-reloads (with cache buster).
  const handleResetAndRetry = async () => {
    if (resetting) return;
    setResetting(true);
    setError('');
    try {
      // 1. Unregister all service workers for this origin.
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      // 2. Purge ALL Cache Storage entries (SW caches and any others).
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
      }
      // 3. Politely clear our own keys from local + session storage.
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('kindra_') || k.includes('auth') || k.includes('token'))
          .forEach((k) => localStorage.removeItem(k));
      } catch { /* noop */ }
      try { sessionStorage.clear(); } catch { /* noop */ }
      // 4. Server-side: clear stale cookies (logout endpoint is idempotent).
      try { await axios.post(`${API}/auth/logout`); } catch { /* noop */ }
    } finally {
      // 5. Hard-reload with a cache buster so the browser pulls fresh HTML/JS.
      const u = new URL(window.location.href);
      u.searchParams.set('_kr', Date.now().toString(36));
      window.location.replace(u.toString());
    }
  };

  const getOnboardingRoute = async () => {
    try {
      const { data: cfg } = await axios.get(`${API}/onboarding-lite/config`);
      return cfg.lite_enabled ? '/onboarding-lite' : '/onboarding';
    } catch { return '/onboarding'; }
  };

  useEffect(() => {
    if (user && user.onboarding_completed) {
      navigate('/home');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (!data.onboarding_completed) {
        const route = await getOnboardingRoute();
        navigate(route);
      } else {
        navigate('/home');
      }
    } catch (err) {
      // Surface the REAL failure state — no generic catch-all.
      // Distinguishing network failures from auth failures is trust-critical.
      if (!err.response) {
        // Request never reached the server (CORS, DNS, offline, CSP, etc.)
        setError("We couldn't reach the Kindra server. Please check your connection and try again — if this keeps happening, tell the Kindra team.");
      } else if (err.response.status === 401) {
        setError('Incorrect email or password.');
      } else if (err.response.status === 429) {
        setError('Too many login attempts. Please wait a few minutes and try again.');
      } else if (err.response.status === 403) {
        setError(formatError(err.response?.data?.detail) || 'Your account is not active. Please contact support.');
      } else if (err.response.status >= 500) {
        setError("Something's wrong on our end. The team has been notified — please try again in a minute.");
      } else {
        setError(formatError(err.response?.data?.detail) || err.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-8"
      style={{
        paddingTop: 'max(env(safe-area-inset-top, 0px), 1rem)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo Section - Full logo asset */}
        <div className="text-center mb-10">
          <img 
            src="/kindra-logo.png" 
            alt="Kindra" 
            className="h-28 mx-auto object-contain mb-6"
          />
          <h2 className="text-2xl font-light text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome back</h2>
          <p className="text-slate-500 mt-1">Sign in to continue your health journey</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {demoBanner && !error && (
            <div
              className="bg-[#235347]/5 border border-[#235347]/20 text-[#235347] px-4 py-3 rounded-xl text-sm leading-relaxed"
              data-testid="login-demo-banner"
            >
              <p className="font-medium">Investor Demo Account</p>
              <p className="text-xs text-[#235347]/80 mt-0.5">
                We've pre-filled the credentials below. Tap <strong>Sign In</strong> to enter Nicholas Hart's fully populated sample account.
              </p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}
          <div>
            <input
              data-testid="login-email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
              required
            />
          </div>
          <div>
            <input
              data-testid="login-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
              required
            />
            <div className="text-right mt-2">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-xs text-slate-500 hover:text-[#235347] transition-colors"
                data-testid="login-forgot-password"
              >
                Forgot password?
              </button>
            </div>
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#235347] text-white rounded-full px-6 py-4 font-medium text-lg hover:bg-[#1A3F36] transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
          >
            {loading && <Spinner size={20} className="animate-spin" />}
            Sign In
          </button>
        </form>

        <div className="text-center mt-8">
          <button
            data-testid="login-nda-link"
            onClick={() => navigate('/confidentiality')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#235347] transition-colors"
          >
            <ShieldCheck size={14} />
            Confidentiality Agreement
          </button>
        </div>

        {/* P0 hotfix — Reset / try again. Visible only after a login attempt failed
            (or user clicks). Quietly fixes stale-SW / stale-cache / stale-cookie
            states that can strand iOS Safari home-screen PWAs on an old bundle. */}
        <div className="text-center mt-3">
          <button
            type="button"
            data-testid="login-reset-link"
            onClick={handleResetAndRetry}
            disabled={resetting}
            className="text-[12px] text-slate-400 hover:text-slate-600 transition-colors underline decoration-slate-200 underline-offset-4 hover:decoration-slate-400 disabled:opacity-50"
          >
            {resetting ? 'Resetting…' : 'Trouble logging in? Reset and try again'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};


export default LoginPage;
